import clsx from "clsx";
import { motion } from "framer-motion";

import { useChromeContext } from "@components/chrome/hooks";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  useMetricsExplorerContext,
  useMetricsExplorerStateContext,
} from "@features/metrics-explorer/hooks";
import { AttributionButton } from "@features/sales-v3/minimized-top-bar/components/attribution-button";
import { useFeatureFlags } from "@hooks/http";
import { type ITimeGranularityV2 } from "@north-beam/nb-common";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { AttributionModelSelect } from "@shared/selects";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { DateFilters } from "../date-filters";
import { SavedViewsDropdown } from "../saved-views/saved-views-dropdown";

export const MinimizedTopBar = () => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRange,
    filterQuery,
    selectedSavedView,
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setTimeGranularity,
    setDateRange,
  } = useMetricsExplorerStateContext();
  const {
    filters: { dateRangeInterval },
  } = useMetricsExplorerContext();
  const { hasFeatureFlag } = useFeatureFlags();
  const { isSidebarExpanded } = useChromeContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    filterQuery,
    dateRange,
  };

  const isAttributionWindowVisible = accountingMode === "accrual";
  const positionTop = hasFeatureFlag("enableDashboardV2") ? 76 : 56;
  const sidebarWidth = hasFeatureFlag("enableDashboardV2")
    ? isSidebarExpanded
      ? 240
      : 73
    : 56;

  return (
    <motion.div
      key="minimized-top-bar"
      initial={{ translateY: -positionTop }}
      exit={{ translateY: -positionTop }}
      animate={{ translateY: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "bg-v2-primary border-b border-v2-secondary py-1 px-2 flex items-center justify-between fixed z-30",
      )}
      style={{ top: positionTop, width: `calc(100% - ${sidebarWidth}px)` }}
    >
      <div className="flex items-center gap-1">
        <div className="flex items-center gap-1">
          <div className="text-lg font-bold m-0 max-w-xxxs truncate">
            {generateAnonBase64Hash({
              value: selectedSavedView?.name,
              prefix: "quick-filter",
              doAnon: isDemoMode && !selectedSavedView?.is_template,
            })}
          </div>
          <SavedViewsDropdown
            buttonClassName={clsx(
              "rounded-full border h-7 w-7 text-xs font-bold flex items-center justify-center",
            )}
            placement="bottom-start"
          />
        </div>
        <div>
          <AttributionModelSelect
            menuClassName="w-[200px]"
            attributionModel={attributionModel}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel: value,
                  pageContext,
                },
              });

              setAttributionModel(value);
            }}
            label=""
            buttonRenderer={(props) => (
              <AttributionButton variant="combobox" label="Model" {...props} />
            )}
          />
        </div>
        {isAttributionWindowVisible && (
          <div>
            <AttributionWindowSelect
              menuClassName="w-[100px]"
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
              timeGranularity="daily"
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Metrics Explorer",
                  action: "onChange",
                  additionalActionInfo: "Set Attribution Window",
                  data: {
                    newAttributionWindow: value,
                    pageContext,
                  },
                });

                setAttributionWindow(value);
              }}
              label=""
              buttonRenderer={(props) => (
                <AttributionButton
                  variant="combobox"
                  label="Window"
                  {...props}
                />
              )}
            />
          </div>
        )}
        <div>
          <NbAccountingModeSelect
            label=""
            menuClassName="w-[180px]"
            nbAccountingMode={accountingMode}
            isLoading={false}
            icon={null}
            buttonRenderer={(props) => (
              <AttributionButton
                variant="combobox"
                label="Accounting"
                {...props}
              />
            )}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Accounting Mode",
                data: {
                  newAccountingMode: value,
                  pageContext,
                },
              });

              setAccountingMode(value);
            }}
          />
        </div>
        <TimeGranularityV2Select
          label=""
          className="text-xs"
          timeGranularity={timeGranularity}
          hideHourlyOption
          onChange={(value: ITimeGranularityV2) => {
            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onChange",
              additionalActionInfo: "Set Time Granularity",
              data: {
                newTimeGranularity: value,
                pageContext,
              },
            });

            setDateRange({
              startDate: dateRangeInterval.startDate,
              endDate: dateRangeInterval.endDate,
              type: "fixed",
            });
            setTimeGranularity(value);
          }}
          menuClassName="w-[100px]"
          width="fit-content"
          icon={null}
          buttonRenderer={(props) => (
            <AttributionButton variant="combobox" label="" {...props} />
          )}
        />
        <DateFilters className="h-7" />
      </div>
    </motion.div>
  );
};
